import { memo, useCallback, useState } from 'react';
import { Text, View, type StyleProp, type TextStyle } from 'react-native';
import { runOnJS, useAnimatedReaction, type SharedValue } from 'react-native-reanimated';

import { layoutLtrStyle } from '../../../../shared/i18n/rtl';

type Parts = {
  cm: number;
  ft: number;
  inches: number;
};

function parts_from_cm(cm_raw: number): Parts {
  const cm = Math.max(0, Math.round(cm_raw));
  const total_in = cm > 0 ? Math.round(cm / 2.54) : 0;
  const ft = Math.floor(total_in / 12);
  const inches = total_in - ft * 12;
  return { cm, ft, inches };
}

type Props = {
  cm: SharedValue<number>;
  metricUnitLabel: string;
  numberStyle?: StyleProp<TextStyle>;
  unitStyle?: StyleProp<TextStyle>;
  slashStyle?: StyleProp<TextStyle>;
  markStyle?: StyleProp<TextStyle>;
};

/**
 * Live `170 cm / 5′ 10″` readout for height wheels.
 * One React update per cm tick — avoids the multi-AnimatedCount digit stacks that crash on fling.
 */
export const PlainCmSlashImperialReadout = memo(function PlainCmSlashImperialReadout({
  cm,
  metricUnitLabel,
  numberStyle,
  unitStyle,
  slashStyle,
  markStyle,
}: Props) {
  const [parts, setParts] = useState<Parts>(() => parts_from_cm(cm.value));

  const apply_parts = useCallback((cm_val: number) => {
    setParts(parts_from_cm(cm_val));
  }, []);

  useAnimatedReaction(
    () => Math.round(cm.value),
    (curr, prev) => {
      if (curr !== prev) {
        runOnJS(apply_parts)(curr);
      }
    },
    [cm, apply_parts],
  );

  return (
    <View
      style={[layoutLtrStyle, { flexDirection: 'row', alignItems: 'baseline', flexShrink: 1 }]}
      collapsable={false}>
      <Text style={[{ writingDirection: 'ltr' }, numberStyle]} allowFontScaling={false}>
        {parts.cm}
      </Text>
      <Text style={unitStyle} allowFontScaling={false}>
        {metricUnitLabel}
      </Text>
      <Text style={[{ writingDirection: 'ltr' }, slashStyle]} allowFontScaling={false}>
        /
      </Text>
      <Text style={[{ writingDirection: 'ltr' }, numberStyle]} allowFontScaling={false}>
        {parts.ft}
      </Text>
      <Text style={[{ writingDirection: 'ltr' }, markStyle]} allowFontScaling={false}>
        ′
      </Text>
      {parts.inches > 0 ? (
        <>
          <Text style={[{ writingDirection: 'ltr' }, numberStyle]} allowFontScaling={false}>
            {parts.inches}
          </Text>
          <Text style={[{ writingDirection: 'ltr' }, markStyle]} allowFontScaling={false}>
            ″
          </Text>
        </>
      ) : null}
    </View>
  );
});
